import { Empty, Select } from 'antd';
import type { BaseOptionType, DefaultOptionType } from 'antd/es/select';
import { BaseSpin } from 'components/modules/Spin';
import { debounce } from 'lodash';
import { ReactElement, ReactNode, useEffect, useMemo, useRef, useState } from 'react';

interface RenderItemParams<ValueType, OptionType> {
    options: OptionType[];
    searchValue?: string;
    value?: ValueType;
    notFoundContent: ReactNode;
    loadMore: ReactNode;
    handleSearch: (searchText: string) => void;
    handlePopupScroll: (e: React.UIEvent<HTMLDivElement>) => void;
}

interface Props<ValueType, OptionType extends BaseOptionType | DefaultOptionType = DefaultOptionType> {
    isSearching?: boolean;
    hasMore: boolean;
    /**
     * SelectScrollが次を呼び出すタイミングを定義するしきい値。
     * デフォルト値は0.8である。
     * これはユーザーが総高さの80％を下回ったときに次が呼び出されることを意味する。
     */
    scrollThreshold?: number;
    /**
     * search関数の呼び出しを、待機時間（ミリ秒）後に遅延させる。
     */
    debounceTimeout?: number;
    options: OptionType[];
    value?: ValueType;
    next: (searchText: string) => void;
    search?: (searchText: string) => void;
    renderItem: (params: RenderItemParams<ValueType, OptionType>) => ReactElement;
}

const defaultScrollThreshold = 0.8;
const defaultDebounceTimeout = 700;

const SelectScroll = <ValueType, OptionType extends BaseOptionType | DefaultOptionType = DefaultOptionType>({
    isSearching,
    hasMore,
    scrollThreshold = defaultScrollThreshold,
    debounceTimeout = defaultDebounceTimeout,
    options = [],
    value,
    next,
    search,
    renderItem,
}: Props<ValueType, OptionType>) => {
    const actionTriggeredRef = useRef(false);

    const [prevDataLength, setPrevDataLength] = useState(options.length);
    const [searchText, setSearchText] = useState('');

    // 検索アクションを遅延実行するためのdebounce関数
    const debounceSearch = useMemo(() => {
        const loadOptions = (value: string) => {
            // 検索がトリガーされた時、状態を更新する
            actionTriggeredRef.current = false;
            setPrevDataLength(options.length);
            search?.(value);
        };

        return debounce(loadOptions, debounceTimeout);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debounceTimeout]);

    /**
     * 検索テキスト変更時のハンドラ
     * @param {string} value セレクトボックス上の入力値。
     */
    const handleSearch = (value: string) => {
        setSearchText(value);
        debounceSearch(value);
    };

    /**
     * 下端までスクロールされたか確認する
     *
     * @param target
     * @param scrollThreshold - @see Props.scrollThreshold
     * @returns
     */
    const isElementAtBottom = (target: HTMLElement, scrollThreshold = defaultScrollThreshold) => {
        let threshold = scrollThreshold;

        if (scrollThreshold < 0 || scrollThreshold > 1) {
            threshold = defaultScrollThreshold;
        }

        // 要素の表示可能な高さ
        const clientHeight = target.clientHeight;
        // 要素のコンテンツが上端から縦にスクロールされたピクセル数
        // 要素の上端がそのスクロール可能なコンテンツの上端に対する位置
        const scrollTop = target.scrollTop;
        // 要素のコンテンツの高さ。オーバーフローにより画面に表示されない部分を含む
        const scrollHeight = target.scrollHeight;

        return scrollTop + clientHeight >= threshold * scrollHeight;
    };

    /**
     * Selectのポップアップスクロール時のハンドラ
     * 下端まで到達した場合、次ページデータを取得する
     */
    const handlePopupScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const target = e.target as HTMLElement;

        // 複数回のトリガーを防ぐため、アクションがすでにトリガーされている場合、即座に戻す
        if (actionTriggeredRef.current) return;

        const isScrollToLast = isElementAtBottom(target, scrollThreshold);

        if (isScrollToLast && hasMore && !isSearching) {
            actionTriggeredRef.current = true;
            next(searchText);
        }
    };

    // 検索中・データ未取得時の表示内容
    const notFoundContent = isSearching ? (
        <CenterSpin />
    ) : options.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_DEFAULT} description={'アプリがありません'} />
    ) : undefined;

    // 無限スクロール時のロード中表示
    const loadMore = hasMore && (
        <Select.Option key='load-more' disabled>
            <CenterSpin />
        </Select.Option>
    );

    useEffect(() => {
        const dataLengthChanged = options.length !== prevDataLength;

        // データが変更されたときにリセットする
        if (dataLengthChanged) {
            actionTriggeredRef.current = false;
            setPrevDataLength(options.length);
        }
    }, [options.length, prevDataLength]);

    return renderItem({
        options,
        searchValue: searchText,
        value,
        loadMore,
        notFoundContent,
        handleSearch,
        handlePopupScroll,
    });
};

const CenterSpin = () => (
    <div style={{ textAlign: 'center' }}>
        <BaseSpin size='small' />
    </div>
);

export default SelectScroll;
